import styled from '@emotion/styled';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';
import React from 'react';

export type TextType =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'subheading-first'
  | 'subheading-second'
  | 'body'
  | 'body-medium'
  | 'body-demibold'
  | 'header24'
  | 'title'
  | 'title-medium'
  | 'title-small'
  | 'body18'
  | 'body16'
  | 'bold-body16'
  | 'body14'
  | 'bold-body14'
  | 'body12'
  | 'bold-body12';

export interface TextProps extends TypographyProps {
  className?: string;
  /**
   * Custom text type for selecting font size
   */
  sz?: TextType;
  /**
   * Whether to apply text truncation
   */
  truncate?: boolean;
}

const TextImpl = React.forwardRef<HTMLElement, TextProps>(
  ({ className, sz, truncate, ...props }, ref) => {
    const sizeCls = sz ? `v2-${sz}` : undefined;
    let rootClassName = className;

    if (className || truncate) {
      rootClassName = `${className ?? ''} ${truncate ? 'truncated-text' : ''}`;
    }

    // Only add tooltip when noWrap is specified and children is a string
    const needsTooltip = props.noWrap && typeof props.children === 'string';

    // Typography element with ref (Typography supports ref forwarding)
    const typographyElement = (
      <Typography ref={ref} className={sizeCls} {...props} />
    );

    if (rootClassName) {
      return (
        <span className={rootClassName}>
          {needsTooltip ? (
            <Tooltip
              classes={{ popper: rootClassName }}
              title={props.children}
              placement="right-start"
              arrow
            >
              {typographyElement}
            </Tooltip>
          ) : (
            typographyElement
          )}
        </span>
      );
    }

    // No wrapper needed
    return needsTooltip ? (
      <Tooltip title={props.children} placement="right-start" arrow>
        {typographyElement}
      </Tooltip>
    ) : (
      typographyElement
    );
  }
);

TextImpl.displayName = 'TextImpl';

/**
 * Customized MUI Typography component.
 *
 * It inherits all properties of a MUI Typography component.
 */
export const Text = styled(TextImpl)`
  font-family: 'Nunito Sans', sans-serif;

  & .${tooltipClasses.tooltip} {
    font-size: 12px;
  }

  /* Headlines */
  .v2-h1 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 700;
    font-size: 1.75rem;
    line-height: 1.2;
  }

  .v2-h2 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 1.2;
  }

  .v2-h3 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 1.25rem;
    line-height: 1.2;
  }

  .v2-h4 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.2;
  }

  /* Subheading first - no uppercase, subtle spacing */
  .v2-subheading-first {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 1rem;
    letter-spacing: 0.02em;
    line-height: 1;
  }

  /* Subheading second - Italic sans-serif */
  .v2-subheading-second {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 500;
    font-style: italic;
    font-size: 1rem;
    line-height: 1.2;
  }

  /* Body copy */
  .v2-body {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400; /* Regular */
    font-size: 0.875rem;
    line-height: 1.3;
  }

  .v2-body-medium {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400; /* Regular */
    font-size: 0.8rem;
    line-height: 1.3;
  }

  /* Body copy with emphasis */
  .v2-body-demibold {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600; /* Demi Bold */
    font-size: 0.875rem;
    line-height: 1.3;
  }

  /* Legacy styles - map to new system where possible */
  .v2-header24 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 1.2;
  }

  .v2-title {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    line-height: 1.2;
  }

  .v2-title-medium {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 0.8rem;
    line-height: 1.2;
  }

  .v2-title-small {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600;
    font-size: 0.75rem;
    line-height: 1.2;
  }

  .v2-body18 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-size: 1.125rem;
    line-height: 1.3;
  }

  .v2-body16 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.3;
  }

  .v2-bold-body16 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600; /* Use Demi Bold instead of Bold */
    font-size: 1rem;
    line-height: 1.3;
  }

  .v2-body14 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-size: 0.875rem;
    line-height: 1.3;
  }

  .v2-bold-body14 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600; /* Use Demi Bold instead of Bold */
    font-size: 0.875rem;
    line-height: 1.3;
  }

  .v2-body12 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 1.3;
  }

  .v2-bold-body12 {
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 600; /* Use Demi Bold instead of Bold */
    font-size: 0.75rem;
    line-height: 1.3;
  }

  &.truncated-text {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export default Text;
